import { useTranslation } from 'next-i18next'
import { useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import Times from '~/bootcamp/Activities/components/Times'

import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'

import useFractionExpandNum from './useFractionExpandNum'

function FractionExpandNum({
  answerState,
  actionButtonsFC,
  autofocus = true,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const {
    question: { leftNum, leftDenom, rightNum },
    answer,
    formValid,
    error,
    onInputChange,
    handleCheckAnswer,
    goToNextQuestion,
  } = useFractionExpandNum({
    difficultyLevel,
    onAnswerStateChange,
    onNextQuestionClick,
  })
  const [inputKey, setInputKey] = useState<string>(uuid())

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEmitInstance(
    onInstanceChange,
    leftDenom
      ? `\\frac{${leftNum} \\cdot \\square}{${leftDenom} \\cdot \\square} = \\frac{${rightNum}}{\\square}`
      : null,
  )

  const handleGoToNextQuestion = (): void => {
    goToNextQuestion()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (formValid) {
      hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="flex items-center space-x-1">
          <div>
            <span className="flex items-center space-x-1 justify-end">
              <span className="text-2xl h-13.5 flex items-end">
                <span>{leftNum}</span>
                <Times />
              </span>
              <Input
                aria-label={t('ariaLabelButtons.insertValue')}
                autoFocus={autofocus}
                isError={error && error.leftNumFactor}
                key={`${inputKey}-first`}
                maxDigits={3}
                onChange={(value) => onInputChange('leftNumFactor', value)}
                onEnter={onEnterPress}
                ref={inputRef}
                value={`${answer.leftNumFactor || ''}`}
              />
            </span>
            <Divider className="my-2" />
            <span className="flex items-center space-x-1 justify-end">
              <span className="text-2xl h-10 sm:h-13.5 flex items-start">
                <span>{leftDenom}</span>
                <Times />
              </span>
              <Input
                aria-label={t('ariaLabelButtons.insertValue')}
                isError={error && error.leftDenomFactor}
                key={`${inputKey}-second`}
                maxDigits={3}
                onChange={(value) => onInputChange('leftDenomFactor', value)}
                onEnter={onEnterPress}
                value={`${answer.leftDenomFactor || ''}`}
              />
            </span>
          </div>
          <Equals />
          <div className="flex flex-col items-center">
            <span className="text-2xl h-13.5 flex items-end">
              <span>{rightNum}</span>
            </span>
            <Divider className="my-2" />
            <Input
              aria-label={t('ariaLabelButtons.insertDenominator')}
              isError={error && error.rightDenom}
              key={`${inputKey}-third`}
              maxDigits={3}
              onChange={(value) => onInputChange('rightDenom', value)}
              onEnter={onEnterPress}
              value={`${answer.rightDenom || ''}`}
            />
          </div>
        </div>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default FractionExpandNum
